import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";
import { i18n } from "../lib/i18n";

/**
 * Recipient Autocomplete Controller
 *
 * Wraps TomSelect for multi-user selection in the compose form.
 * - Configures remote URL to search users endpoint
 * - Auto-excludes current user via exclude_ids parameter
 * - Dispatches recipients:changed custom event with selected user IDs
 * - Renders selected users as removable chips
 */
export default class extends Controller {
  static values = {
    remoteUrl: String,
    currentUserId: String,
    placeholder: { type: String, default: "Search for colleagues..." },
  };

  static targets = ["select"];

  declare readonly remoteUrlValue: string;
  declare readonly currentUserIdValue: string;
  declare readonly placeholderValue: string;
  declare readonly selectTarget: HTMLSelectElement;
  declare readonly hasSelectTarget: boolean;

  private tomSelect: TomSelect | null = null;

  connect() {
    this.initializeTomSelect();
  }

  disconnect() {
    if (this.tomSelect) {
      this.tomSelect.destroy();
      this.tomSelect = null;
    }
  }

  private initializeTomSelect() {
    const selectElement = this.hasSelectTarget
      ? this.selectTarget
      : (this.element as HTMLSelectElement);

    // Turbo reconnects on DOM nodes that survived the visit. Adopt the live
    // instance rather than rebuilding: destroy() reverts the element to the
    // options it was built with, dropping anything the remote load added.
    const existing = (selectElement as any).tomselect;
    if (existing) {
      this.tomSelect = existing;
      return;
    }

    this.tomSelect = new TomSelect(selectElement, {
      plugins: {
        remove_button: {
          title: "Remove",
        },
      },
      valueField: "id",
      labelField: "name",
      searchField: ["name"],
      maxItems: 10,
      placeholder: this.placeholderValue,
      load: this.loadOptions.bind(this),
      shouldLoad: () => true,
      onFocus: () => {
        this.tomSelect?.load("");
      },
      render: {
        option: (data: any, escape: any) => {
          if (data.option_html) {
            return data.option_html;
          }
          return `<div class="text-sm">${escape(data.name)}</div>`;
        },
        item: (data: any, escape: any) => {
          return `<div class="text-sm">${escape(data.name)}</div>`;
        },
        no_results: () => {
          return `<div class="text-base text-center text-gray-800">${i18n.t('js.recipient_autocomplete.no_results')}</div>`;
        },
      },
      onChange: this.handleChange.bind(this),
    });
  }

  private async loadOptions(query: string, callback: (data: any[]) => void) {
    if (!this.remoteUrlValue) {
      callback([]);
      return;
    }

    const url = new URL(this.remoteUrlValue, window.location.origin);
    url.searchParams.append("q", query);

    if (this.currentUserIdValue) {
      url.searchParams.append("exclude_ids", this.currentUserIdValue);
    }

    try {
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();
      callback(data);
    } catch (error) {
      console.error("Failed to load recipients:", error);
      callback([]);
    }
  }

  private handleChange() {
    const selectedIds = this.tomSelect?.items || [];

    this.dispatch("changed", {
      detail: { recipientIds: selectedIds },
      bubbles: true,
    });
  }

  get selectedIds(): string[] {
    return this.tomSelect?.items || [];
  }
}
